import { useState, useMemo } from 'react';
import { List, Show, Create, Edit, SimpleForm, TextInput, SelectInput, DateInput, BooleanInput, Datagrid, TextField, DateField, ChipField, useGetList, useNavigate, useRecordContext, useRefresh, useNotify, useUpdate, ReferenceInput } from 'react-admin';
import { Chip, Box, Typography, Paper, Grid, Button, IconButton, Alert, Divider, Table, TableBody, TableCell, TableHead, TableRow, Avatar, LinearProgress } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { EmployeeRecord, DepartmentRecord } from '@/resources/hr/types';
import { Assignment, CheckCircle, Schedule, Warning, Close, Visibility, Edit as EditIcon, Add, FolderOpen, Description, Event, CalendarMonth } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ListPageHeader } from '@/components/ListPageHeader';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';

interface EmploymentContractItem {
  id: string;
  status: string;
  contractType: string;
  contractNumber: string;
  employeeName: string;
  departmentName?: string;
  department?: string;
  roleTitle: string;
  startDate: string;
  endDate?: string;
  probationEndDate?: string;
  noticePeriodWeeks?: number;
  annualLeaveDays?: number;
  sickLeaveDays?: number;
  remoteWorkAllowed?: boolean;
  signedAt?: string;
  employee?: { id: string };
}

const CONTRACT_TYPES = ['Permanent', 'FixedTerm', 'PartTime', 'Seasonal', 'IndependentContractor'] as const;
const CONTRACT_STATUSES = ['Draft', 'PendingSignature', 'Active', 'Expired', 'Terminated', 'Renewed'] as const;
const SENIORITY_LEVELS = ['Top Management', 'Senior Management', 'Professionally Qualified', 'Skilled Technical', 'Semi-Skilled', 'Unskilled'];

const STATUS_COLORS: Record<string, string> = {
  Draft: 'default',
  PendingSignature: 'warning',
  Active: 'success',
  Expired: 'default',
  Terminated: 'error',
  Renewed: 'info',
};

const STATUS_ICONS: Record<string, React.ReactElement> = {
  Draft: <EditIcon sx={{ fontSize: 16 }} />,
  PendingSignature: <Schedule sx={{ fontSize: 16 }} />,
  Active: <CheckCircle sx={{ fontSize: 16 }} />,
  Expired: <Event sx={{ fontSize: 16 }} />,
  Terminated: <Warning sx={{ fontSize: 16 }} />,
  Renewed: <CheckCircle sx={{ fontSize: 16 }} />,
};

export const EmploymentContractList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: contracts, isLoading } = useGetList('employment-contracts', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [searchText, setSearchText] = useState('');
  const employeeMap = useEmployeeMap();

  const [now] = useState(() => Date.now());

  const filteredContracts = useMemo(() => {
    if (!contracts) return [];
    return contracts.filter((contract: EmploymentContractItem) => {
      if (filterStatus && contract.status !== filterStatus) return false;
      if (filterType && contract.contractType !== filterType) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        const employeeName = getEmployeeName(employeeMap, contract.employee?.id).toLowerCase();
        if (!employeeName.includes(search) &&
            !contract.contractNumber?.toLowerCase().includes(search)) return false;
      }
      if (expiringSoon) {
        const endDate = contract.endDate ? new Date(contract.endDate).getTime() : null;
        const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
        if (!endDate || endDate - now > ninetyDaysMs) return false;
      }
      return true;
    });
  }, [contracts, filterStatus, filterType, expiringSoon, searchText, employeeMap]);

  const stats = useMemo(() => {
    if (!contracts) return { total: 0, active: 0, draft: 0, expiring: 0, expired: 0 };
    const active = contracts.filter((c: EmploymentContractItem) => c.status === 'Active').length;
    const draft = contracts.filter((c: EmploymentContractItem) => c.status === 'Draft' || c.status === 'PendingSignature').length;
    const expired = contracts.filter((c: EmploymentContractItem) => c.status === 'Expired' || c.status === 'Terminated').length;
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    const expiring = contracts.filter((c: EmploymentContractItem) => {
      if (c.status !== 'Active' || !c.endDate) return false;
      const endDate = new Date(c.endDate).getTime();
      return endDate > now && endDate - now <= ninetyDaysMs;
    }).length;
    return { total: contracts.length, active, draft, expiring, expired };
  }, [contracts]);

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Employment Contracts"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Draft/Pending', value: String(stats.draft), color: 'warning.main' },
          { label: 'Expiring Soon', value: String(stats.expiring), color: 'error.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextInput
            source="search"
            placeholder="Search by employee or contract number..."
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
            sx={{ minWidth: 300 }}
          />
          <SelectInput
            source="status"
            choices={[{ id: '', name: 'All Status' }, ...CONTRACT_STATUSES.map(s => ({ id: s, name: s }))]}
            onChange={(e) => { const ev = e as React.ChangeEvent<HTMLInputElement>; setFilterStatus(ev.target.value); }}
            sx={{ minWidth: 150 }}
          />
          <SelectInput
            source="contractType"
            choices={[{ id: '', name: 'All Types' }, ...CONTRACT_TYPES.map(t => ({ id: t, name: t }))]}
            onChange={(e) => { const ev = e as React.ChangeEvent<HTMLInputElement>; setFilterType(ev.target.value); }}
            sx={{ minWidth: 150 }}
          />
          <Button
            variant={expiringSoon ? 'contained' : 'outlined'}
            color="warning"
            startIcon={<Event />}
            onClick={() => setExpiringSoon(!expiringSoon)}
          >
            Expiring Soon
          </Button>
          <Button
            component={Link}
            to="/employment-contracts/create"
            variant="contained"
            startIcon={<Add />}
          >
            Create Contract
          </Button>
        </Box>
      </Paper>

      {isLoading ? (
        <LinearProgress />
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contract #</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Probation</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts.map((contract: EmploymentContractItem) => {
                const hasProbation = !!contract.probationEndDate;
                const probationPassed = hasProbation && new Date(contract.probationEndDate!).getTime() < now;
                const isExpiringSoon = !!contract.endDate && new Date(contract.endDate).getTime() - now <= 90 * 24 * 60 * 60 * 1000 && new Date(contract.endDate).getTime() > now;
                const employeeName = getEmployeeName(employeeMap, contract.employee?.id);

                return (
                  <TableRow key={contract.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {contract.contractNumber || contract.id.split(':')[1]?.substring(0, 8).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                          {employeeName?.[0] || '?'}
                        </Avatar>
                        <Typography variant="body2">{employeeName || '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {contract.departmentName || contract.department || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={contract.contractType || 'Permanent'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={STATUS_ICONS[contract.status]}
                        label={contract.status}
                        size="small"
                        color={STATUS_COLORS[contract.status] as ChipColor}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {contract.startDate ? new Date(contract.startDate).toLocaleDateString('en-GB') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isExpiringSoon && <Warning sx={{ fontSize: 14, color: 'warning.main' }} />}
                        <Typography variant="body2" color={isExpiringSoon ? 'warning.main' : 'text.secondary'}>
                          {contract.endDate ? new Date(contract.endDate).toLocaleDateString('en-GB') : 'Permanent'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {hasProbation ? (
                        <Chip
                          label={probationPassed ? 'Passed' : new Date(contract.probationEndDate!).toLocaleDateString('en-GB')}
                          size="small"
                          color={probationPassed ? 'success' : 'warning'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" component={Link} to={`/employment-contracts/${contract.id}/show`}>
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton size="small" component={Link} to={`/employment-contracts/${contract.id}/edit`}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {filteredContracts.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <FolderOpen sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No employment contracts found</Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export const EmploymentContractShow = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [now] = useState(() => Date.now());

  if (!record) return null;

  const hasProbation = record.probationEndDate;
  const probationPassed = hasProbation && new Date(record.probationEndDate).getTime() < now;
  const isExpiringSoon = record.endDate && new Date(record.endDate).getTime() - now <= 90 * 24 * 60 * 60 * 1000 && new Date(record.endDate).getTime() > now;

  const isDraft = record.status === 'Draft';
  const isPendingSignature = record.status === 'PendingSignature';
  const isActive = record.status === 'Active';

  const handleActivate = async () => {
    try {
      await update('employment-contracts', {
        id: record.id,
        data: { status: 'Active' },
      });
      notify('Contract activated', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to activate contract', { type: 'error' });
    }
  };

  const handleMarkSigned = async () => {
    try {
      await update('employment-contracts', {
        id: record.id,
        data: { status: 'Active', signedAt: new Date().toISOString(), signedBy: 'Current User' },
      });
      notify('Contract marked as signed', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to mark contract as signed', { type: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            {record.employeeName?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {record.contractNumber || 'Employment Contract'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {record.employeeName || 'Employee'} — {record.contractType || 'Permanent'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => navigate('/employment-contracts')}>
          <Close />
        </IconButton>
      </Box>

      {record.status === 'Draft' && (
        <Alert severity="info" sx={{ mb: 2 }}>Contract is in draft status and has not been signed.</Alert>
      )}
      {record.status === 'PendingSignature' && (
        <Alert severity="warning" sx={{ mb: 2 }}>Contract awaiting employee signature.</Alert>
      )}
      {isExpiringSoon && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          This contract expires on {record.endDate ? new Date(record.endDate).toLocaleDateString('en-GB') : '—'}. Consider renewal.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Contract Number', value: record.contractNumber },
          { label: 'Employee', value: record.employeeName },
          { label: 'Department', value: record.departmentName || record.department },
          { label: 'Role', value: record.roleTitle },
          { label: 'Contract Type', value: record.contractType },
          { label: 'Status', value: record.status, chip: true, color: STATUS_COLORS[record.status] },
          { label: 'Start Date', value: record.startDate ? new Date(record.startDate).toLocaleDateString('en-GB') : '—' },
          { label: 'End Date', value: record.endDate ? new Date(record.endDate).toLocaleDateString('en-GB') : 'Permanent' },
          { label: 'Probation End', value: record.probationEndDate ? new Date(record.probationEndDate).toLocaleDateString('en-GB') : 'N/A' },
          { label: 'Notice Period', value: record.noticePeriodWeeks ? `${record.noticePeriodWeeks} weeks` : '4 weeks (BCEA)' },
          { label: 'Annual Leave', value: record.annualLeaveDays ? `${record.annualLeaveDays} days` : '—' },
          { label: 'Remote Work', value: record.remoteWorkAllowed ? 'Yes' : 'No' },
          { label: 'Signed At', value: record.signedAt ? new Date(record.signedAt).toLocaleString('en-GB') : 'Not signed' },
        ].map((field) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{field.label}</Typography>
              {field.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={field.value} size="small" color={field.color as ChipColor} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mt: 0.5 }}>{field.value}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {hasProbation && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Probation Status</Typography>
          {probationPassed ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Probation period passed on {new Date(record.probationEndDate).toLocaleDateString('en-GB')}
            </Alert>
          ) : (
            <Alert severity="info" icon={<Schedule />}>
              Probation ends on {new Date(record.probationEndDate).toLocaleDateString('en-GB')}
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>BCEA Compliance Notice</Typography>
        <Typography variant="body2" color="text.secondary">
          Per BCEA Section 38: An employee may terminate a contract with notice of not less than
          {record.noticePeriodWeeks ? ` ${record.noticePeriodWeeks} weeks` : ' 4 weeks'}.
          {' '}Section 42 Certificate of Service must be issued on termination.
        </Typography>
      </Paper>

      {isDraft && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleMarkSigned}
          >
            Mark as Signed
          </Button>
        </Box>
      )}

      {isPendingSignature && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleMarkSigned}
          >
            Confirm Signature
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button component={Link} to="/employees" variant="outlined" startIcon={<Description />}>
          View Employee
        </Button>
        <Button component={Link} to="/employment-contracts" variant="outlined">
          All Contracts
        </Button>
      </Box>
    </Box>
  );
};

export const EmploymentContractCreate = () => {
  const { data: employees } = useGetList('employees', { pagination: { page: 1, perPage: 100 } });
  const { data: departments } = useGetList('departments', { pagination: { page: 1, perPage: 50 } });

  const [employeeId, setEmployeeId] = useState('');
  const [contractType, setContractType] = useState('Permanent');

  const selectedEmployee = useMemo(() => {
    if (!employees || !employeeId) return null;
    return employees.find((e: EmployeeRecord) => `${e.legalEntity?.firstName ?? ''} ${e.legalEntity?.lastName ?? ''}`.trim() === employeeId);
  }, [employees, employeeId]);

  const getDefaultProbation = (startDate: string) => {
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + 3);
    return start.toISOString().split('T')[0];
  };

  return (
    <Create>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>Create Employment Contract</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ReferenceInput source="employee.id" reference="employees" fullWidth>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>

          <SelectInput
            source="contractType"
            choices={CONTRACT_TYPES.map(t => ({ id: t, name: t }))}
            fullWidth
            defaultValue="Permanent"
            onChange={(e) => { const ev = e as React.ChangeEvent<HTMLInputElement>; setContractType(ev.target.value); }}
          />

          <SelectInput
            source="departmentName"
            label="Department"
            choices={departments?.map((d: DepartmentRecord) => ({ id: d.name, name: d.name })) || []}
            fullWidth
          />

          <TextInput source="roleTitle" label="Role / Position" fullWidth />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <DateInput source="startDate" label="Start Date" fullWidth />
            {contractType !== 'Permanent' && (
              <DateInput source="endDate" label="End Date" fullWidth />
            )}
          </Box>

          <DateInput
            source="probationEndDate"
            label="Probation End Date"
            fullWidth
            helperText="Defaults to 3 months from start date"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <TextInput source="noticePeriodWeeks" label="Notice Period (weeks)" type="number" defaultValue={4} fullWidth />
            <TextInput source="annualLeaveDays" label="Annual Leave (days)" type="number" defaultValue={15} fullWidth />
            <TextInput source="sickLeaveDays" label="Sick Leave (days)" type="number" defaultValue={12} fullWidth />
          </Box>

          <BooleanInput source="remoteWorkAllowed" label="Remote Work Allowed" defaultValue={false} />

          <Alert severity="info">
            BCEA defaults: 4 weeks notice period, 15 days annual leave, 12 days sick leave.
            Permanent contracts have no end date.
          </Alert>
        </Box>
      </SimpleForm>
    </Create>
  );
};

export const EmploymentContractEdit = () => {
  const record = useRecordContext();
  const canEdit = record?.status === 'Draft' || record?.status === 'PendingSignature';

  return (
    <Edit>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>Edit Employment Contract</Typography>

        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This contract cannot be edited while {record?.status?.toLowerCase()}. Contact administrator for corrections.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <TextInput source="contractNumber" label="Contract Number" fullWidth disabled />
          <TextInput source="employee.name" label="Employee" fullWidth disabled={!canEdit} />
          <SelectInput source="contractType" choices={CONTRACT_TYPES.map(t => ({ id: t, name: t }))} fullWidth disabled={!canEdit} />
          <SelectInput source="status" choices={CONTRACT_STATUSES.map(s => ({ id: s, name: s }))} fullWidth disabled />
          <TextInput source="departmentName" label="Department" fullWidth disabled={!canEdit} />
          <TextInput source="roleTitle" label="Role" fullWidth disabled={!canEdit} />
          <DateInput source="startDate" label="Start Date" fullWidth disabled={!canEdit} />
          <DateInput source="endDate" label="End Date" fullWidth disabled={!canEdit} />
          <DateInput source="probationEndDate" label="Probation End Date" fullWidth disabled={!canEdit} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <TextInput source="noticePeriodWeeks" label="Notice Period (weeks)" type="number" fullWidth disabled={!canEdit} />
          <TextInput source="annualLeaveDays" label="Annual Leave (days)" type="number" fullWidth disabled={!canEdit} />
          <TextInput source="sickLeaveDays" label="Sick Leave (days)" type="number" fullWidth disabled={!canEdit} />
        </Box>

        <BooleanInput source="remoteWorkAllowed" label="Remote Work Allowed" disabled={!canEdit} />
      </SimpleForm>
    </Edit>
  );
};